import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import ReportGenerator from './ReportGenerator';

export default function GainsReport() {
  const [monthlyData, setMonthlyData] = useState([]);
  const [clientData, setClientData] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [monthly, clients, summaryRes] = await Promise.all([
        axios.get('/api/gains/monthly'),
        axios.get('/api/gains/by-client'),
        axios.get('/api/gains/summary')
      ]);

      setMonthlyData(monthly.data.reverse());
      setClientData(clients.data);
      setSummary(summaryRes.data);
    } catch (err) {
      console.error('Error cargando reportes:', err);
    } finally {
      setLoading(false);
    }
  };

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>⏳ Cargando reportes...</div>;
  }

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0 }}>💰 Reportes de Ganancias</h2>
        <ReportGenerator
          data={{ summary, clients: clientData }}
          type="gains"
          fileName={`ganancias_${new Date().toISOString().split('T')[0]}`}
        />
      </div>

      {/* Tarjetas de resumen */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '30px' }}>
        <div style={{ padding: '15px', backgroundColor: '#f0f9ff', borderRadius: '8px', borderLeft: '4px solid #3b82f6' }}>
          <p style={{ margin: 0, color: '#6b7280', fontSize: '12px' }}>Total Prestado</p>
          <h3 style={{ margin: '10px 0 0 0', color: '#1f2937' }}>
            ${((summary.total_loaned || 0) / 1000000).toFixed(1)}M
          </h3>
        </div>

        <div style={{ padding: '15px', backgroundColor: '#f0fdf4', borderRadius: '8px', borderLeft: '4px solid #10b981' }}>
          <p style={{ margin: 0, color: '#6b7280', fontSize: '12px' }}>Total Ganancias</p>
          <h3 style={{ margin: '10px 0 0 0', color: '#1f2937' }}>
            ${((summary.total_gains || 0) / 1000000).toFixed(1)}M
          </h3>
        </div>

        <div style={{ padding: '15px', backgroundColor: '#fef3c7', borderRadius: '8px', borderLeft: '4px solid #f59e0b' }}>
          <p style={{ margin: 0, color: '#6b7280', fontSize: '12px' }}>Total Préstamos</p>
          <h3 style={{ margin: '10px 0 0 0', color: '#1f2937' }}>
            {summary.total_loans || 0}
          </h3>
        </div>

        <div style={{ padding: '15px', backgroundColor: '#fef2f2', borderRadius: '8px', borderLeft: '4px solid #ef4444' }}>
          <p style={{ margin: 0, color: '#6b7280', fontSize: '12px' }}>Total Clientes</p>
          <h3 style={{ margin: '10px 0 0 0', color: '#1f2937' }}>
            {summary.total_clients || 0}
          </h3>
        </div>
      </div>

      {/* Gráfico de Ganancias Mensuales */}
      <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', marginBottom: '30px', border: '1px solid #e5e7eb' }}>
        <h3>📈 Ganancias Mensuales</h3>
        {monthlyData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mes" />
              <YAxis />
              <Tooltip formatter={(value) => `$${value.toLocaleString('es-CO')}`} />
              <Legend />
              <Bar dataKey="total_gains" fill="#10b981" name="Ganancias" />
              <Bar dataKey="total_loaned" fill="#3b82f6" name="Monto Prestado" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p style={{ color: '#9ca3af' }}>Sin datos mensuales</p>
        )}
      </div>

      {/* Ganancias por Cliente */}
      <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
        <h3>👥 Top 10 Clientes por Ganancias</h3>
        {clientData.length > 0 ? (
          <>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={clientData.slice(0, 10)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} interval={0} />
                <YAxis />
                <Tooltip formatter={(value) => `$${value.toLocaleString('es-CO')}`} />
                <Bar dataKey="total_gains" fill="#f59e0b" name="Ganancias" />
              </BarChart>
            </ResponsiveContainer>

            <table style={{ width: '100%', marginTop: '20px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f3f4f6', borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Cliente</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>Préstamos</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>Monto Total</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>Ganancias</th>
                </tr>
              </thead>
              <tbody>
                {clientData.map((client, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '10px' }}>
                      <strong>{client.name}</strong>
                      <br />
                      <small style={{ color: '#6b7280' }}>{client.cedula}</small>
                    </td>
                    <td style={{ padding: '10px', textAlign: 'right' }}>{client.total_loans || 0}</td>
                    <td style={{ padding: '10px', textAlign: 'right' }}>
                      ${((client.total_loaned || 0) / 1000).toFixed(0)}K
                    </td>
                    <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold', color: '#10b981' }}>
                      ${((client.total_gains || 0) / 1000).toFixed(0)}K
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : (
          <p style={{ color: '#9ca3af' }}>Sin clientes activos</p>
        )}
      </div>
    </div>
  );
}
